@import "tailwindcss";
@import "tw-animate-css";

@custom-variant dark (&:is(.dark *));

@theme {
  --font-sans: "Nunito", ui-sans-serif, system-ui, sans-serif,
    "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji";

  --color-warm-gray-50: oklch(0.98 0.0017 67.8);
  --color-warm-gray-100: oklch(0.96 0.0034 67.78);
  --color-warm-gray-200: oklch(0.95 0.0052 67.76);
  --color-warm-gray-300: oklch(0.89 0.0087 67.71);
  --color-warm-gray-400: oklch(0.84 0.0129 75.35);
  --color-warm-gray-500: oklch(0.78 0.0138 71.29);
  --color-warm-gray-600: oklch(0.68 0.0124 71.84);
  --color-warm-gray-700: oklch(0.58 0.009 73.69);
  --color-warm-gray-800: oklch(0.47 0.0071 59.53);
  --color-warm-gray-900: oklch(0.36 0.0044 67.69);

  --color-green-50: oklch(0.98 0.0207 166.11);
  --color-green-100: oklch(0.95 0.0507 163.05);
  --color-green-200: oklch(0.9 0.0895 164.15);
  --color-green-300: oklch(0.85 0.1299 164.98);
  --color-green-400: oklch(0.77 0.1535 163.22);
  --color-green-500: oklch(0.7 0.1491 162.48);
  --color-green-600: oklch(0.6 0.1274 163.23);
  --color-green-700: oklch(0.51 0.1049 165.61);
  --color-green-800: oklch(0.43 0.0865 166.91);
  --color-green-900: oklch(0.38 0.073 168.94);

  --color-purple-50: oklch(0.98 0.013 310.53);
  --color-purple-100: oklch(0.95 0.0299 307.12);
  --color-purple-200: oklch(0.91 0.0546 306.68);
  --color-purple-300: oklch(0.83 0.0966 306.49);
  --color-purple-400: oklch(0.73 0.16 305.86);
  --color-purple-500: oklch(0.63 0.2111 304.75);
  --color-purple-600: oklch(0.56 0.2308 303.39);
  --color-purple-700: oklch(0.49 0.2148 303.47);
  --color-purple-800: oklch(0.44 0.1772 305.12);
  --color-purple-900: oklch(0.38 0.1476 306.22);
  --color-purple-950: oklch(0.29 0.1336 303.68);

  --shadow-sm-hard: rgba(0, 0, 0, 0.15) 0px 2px 8px,
    rgba(0, 0, 0, 0.1) 0px 1px 1px 0px;
  --shadow-sm-soft: rgba(0, 0, 0, 0.04) 0px 0px 1px 1px,
    rgba(0, 0, 0, 0.05) 0px 2px 3px, rgba(0, 0, 0, 0.06) 0px 3px 6px;

  --drop-shadow-navbar: 0 1px 2px rgba(0, 0, 0, 0.2);
}

html,
body {
  @apply bg-white dark:bg-gray-950;
}

@theme inline {
  --radius-sm: calc(var(--radius) - 4px);
  --radius-md: calc(var(--radius) - 2px);
  --radius-lg: var(--radius);
  --radius-xl: calc(var(--radius) + 4px);
  --color-background: var(--background);
  --color-foreground: var(--foreground);
  --color-card: var(--card);
  --color-card-foreground: var(--card-foreground);
  --color-popover: var(--popover);
  --color-popover-foreground: var(--popover-foreground);
  --color-primary: var(--primary);
  --color-primary-foreground: var(--primary-foreground);
  --color-secondary: var(--secondary);
  --color-secondary-foreground: var(--secondary-foreground);
  --color-muted: var(--muted);
  --color-muted-foreground: var(--muted-foreground);
  --color-accent: var(--accent);
  --color-accent-foreground: var(--accent-foreground);
  --color-destructive: var(--destructive);
  --color-border: var(--border);
  --color-input: var(--input);
  --color-ring: var(--ring);
  --color-chart-1: var(--chart-1);
  --color-chart-2: var(--chart-2);
  --color-chart-3: var(--chart-3);
  --color-chart-4: var(--chart-4);
  --color-chart-5: var(--chart-5);
  --color-sidebar: var(--sidebar);
  --color-sidebar-foreground: var(--sidebar-foreground);
  --color-sidebar-primary: var(--sidebar-primary);
  --color-sidebar-primary-foreground: var(--sidebar-primary-foreground);
  --color-sidebar-accent: var(--sidebar-accent);
  --color-sidebar-accent-foreground: var(--sidebar-accent-foreground);
  --color-sidebar-border: var(--sidebar-border);
  --color-sidebar-ring: var(--sidebar-ring);
}

:root {
  --radius: 0.625rem;
  --background: oklch(1 0 0);
  --foreground: oklch(0.145 0 0);
  --card: oklch(1 0 0);
  --card-foreground: oklch(0.145 0 0);
  --popover: oklch(1 0 0);
  --popover-foreground: oklch(0.145 0 0);
  --primary: oklch(0.205 0 0);
  --primary-foreground: oklch(0.985 0 0);
  --secondary: oklch(0.97 0 0);
  --secondary-foreground: oklch(0.205 0 0);
  --muted: oklch(0.97 0 0);
  --muted-foreground: oklch(0.556 0 0);
  --accent: oklch(0.97 0 0);
  --accent-foreground: oklch(0.205 0 0);
  --destructive: oklch(0.577 0.245 27.325);
  --border: oklch(0.922 0 0);
  --input: oklch(0.922 0 0);
  --ring: oklch(0.708 0 0);
  --chart-1: oklch(0.646 0.222 41.116);
  --chart-2: oklch(0.6 0.118 184.704);
  --chart-3: oklch(0.398 0.07 227.392);
  --chart-4: oklch(0.828 0.189 84.429);
  --chart-5: oklch(0.769 0.188 70.08);
  --sidebar: oklch(0.985 0 0);
  --sidebar-foreground: oklch(0.145 0 0);
  --sidebar-primary: oklch(0.205 0 0);
  --sidebar-primary-foreground: oklch(0.985 0 0);
  --sidebar-accent: oklch(0.97 0 0);
  --sidebar-accent-foreground: oklch(0.205 0 0);
  --sidebar-border: oklch(0.922 0 0);
  --sidebar-ring: oklch(0.708 0 0);
}

.dark {
  --background: oklch(0.145 0 0);
  --foreground: oklch(0.985 0 0);
  --card: oklch(0.205 0 0);
  --card-foreground: oklch(0.985 0 0);
  --popover: oklch(0.205 0 0);
  --popover-foreground: oklch(0.985 0 0);
  --primary: oklch(0.922 0 0);
  --primary-foreground: oklch(0.205 0 0);
  --secondary: oklch(0.269 0 0);
  --secondary-foreground: oklch(0.985 0 0);
  --muted: oklch(0.269 0 0);
  --muted-foreground: oklch(0.708 0 0);
  --accent: oklch(0.269 0 0);
  --accent-foreground: oklch(0.985 0 0);
  --destructive: oklch(0.704 0.191 22.216);
  --border: oklch(1 0 0 / 10%);
  --input: oklch(1 0 0 / 15%);
  --ring: oklch(0.556 0 0);
  --chart-1: oklch(0.488 0.243 264.376);
  --chart-2: oklch(0.696 0.17 162.48);
  --chart-3: oklch(0.769 0.188 70.08);
  --chart-4: oklch(0.627 0.265 303.9);
  --chart-5: oklch(0.645 0.246 16.439);
  --sidebar: oklch(0.205 0 0);
  --sidebar-foreground: oklch(0.985 0 0);
  --sidebar-primary: oklch(0.488 0.243 264.376);
  --sidebar-primary-foreground: oklch(0.985 0 0);
  --sidebar-accent: oklch(0.269 0 0);
  --sidebar-accent-foreground: oklch(0.985 0 0);
  --sidebar-border: oklch(1 0 0 / 10%);
  --sidebar-ring: oklch(0.556 0 0);
}

@layer base {
  * {
    @apply border-border outline-ring/50;
  }
  body {
    @apply bg-background text-foreground;
  }
}
